import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save, UserPlus, AlertTriangle } from 'lucide-react';
import { usePatient, usePatientMutations } from '../../hooks/usePatients';
import { usePatientRiskFactors } from '../../hooks/usePatientRiskFactors';
import { 
  departmentService, 
  roomService, 
  bedService 
} from '../../services/firebaseService';
import './PatientForm.css';

const PatientForm = ({ patientId, onClose, onSave, defaultDepartmentId, defaultRoomId, defaultBedId }) => {
  const { t } = useTranslation('patientRisk');
  const { patient, loading: patientLoading } = usePatient(patientId);
  const { createPatient, updatePatient, saving } = usePatientMutations();
  const { factors: availableRiskFactors, loading: factorsLoading } = usePatientRiskFactors({ activeOnly: true });
  
  const [formData, setFormData] = useState({
    patientIdentifier: '',
    bedId: defaultBedId || '',
    roomId: defaultRoomId || '',
    departmentId: defaultDepartmentId || '',
    admissionDate: new Date().toISOString().split('T')[0],
    expectedDischargeDate: '',
    conditions: [],
    isolationLevel: 'None',
    ageRange: '18-65',
    riskFactorIds: [],
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [departments, setDepartments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  
  // Load clinical data
  useEffect(() => {
    const loadData = async () => {
      const depts = await departmentService.getAll();
      setDepartments(depts);
    };
    loadData();
  }, []);
  
  // Load rooms when department changes
  useEffect(() => {
    const loadRooms = async () => {
      if (formData.departmentId) {
        const allRooms = await roomService.getAll();
        const deptRooms = allRooms.filter(r => r.departmentId === formData.departmentId);
        setRooms(deptRooms);
      } else {
        setRooms([]);
      }
    };
    loadRooms();
  }, [formData.departmentId]);
  
  // Load beds when room changes
  useEffect(() => {
    const loadBeds = async () => {
      if (formData.roomId) {
        const allBeds = await bedService.getAll();
        const roomBeds = allBeds.filter(b => b.roomId === formData.roomId);
        setBeds(roomBeds);
      } else {
        setBeds([]);
      }
    };
    loadBeds();
  }, [formData.roomId]);
  
  // Populate form when editing existing patient
  useEffect(() => {
    if (patient && !patientLoading) {
      setFormData({
        patientIdentifier: patient.patientIdentifier || '',
        bedId: patient.bedId || '',
        roomId: patient.roomId || '',
        departmentId: patient.departmentId || '',
        admissionDate: patient.admissionDate ? new Date(patient.admissionDate.toDate()).toISOString().split('T')[0] : '',
        expectedDischargeDate: patient.expectedDischargeDate ? new Date(patient.expectedDischargeDate.toDate()).toISOString().split('T')[0] : '',
        conditions: patient.conditions || [],
        isolationLevel: patient.isolationLevel || 'None',
        ageRange: patient.ageRange || '18-65',
        riskFactorIds: patient.riskFactorIds || [],
        notes: patient.notes || ''
      });
    }
  }, [patient, patientLoading]);
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };
  
  const handleDepartmentChange = (departmentId) => {
    setFormData(prev => ({
      ...prev,
      departmentId,
      roomId: '', // Reset room and bed when department changes
      bedId: ''
    }));
  };
  
  const handleRoomChange = (roomId) => {
    setFormData(prev => ({
      ...prev,
      roomId,
      bedId: '' // Reset bed when room changes
    }));
  };
  
  const handleRiskFactorToggle = (factorId) => {
    const currentFactors = formData.riskFactorIds || [];
    const isSelected = currentFactors.includes(factorId);
    
    if (isSelected) {
      handleInputChange('riskFactorIds', currentFactors.filter(id => id !== factorId));
    } else {
      handleInputChange('riskFactorIds', [...currentFactors, factorId]);
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.patientIdentifier?.trim()) {
      newErrors.patientIdentifier = t('patientIdentifierRequired');
    }
    
    if (!formData.departmentId) {
      newErrors.departmentId = t('departmentRequired');
    }
    
    if (!formData.roomId) {
      newErrors.roomId = t('roomRequired');
    }
    
    if (!formData.bedId) {
      newErrors.bedId = t('bedRequired');
    }
    
    if (!formData.admissionDate) {
      newErrors.admissionDate = t('admissionDateRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSave = async () => {
    console.log('🔍 handleSave called');
    
    if (!validateForm()) {
      console.log('❌ Form validation failed', errors);
      return;
    }
    
    console.log('✅ Form validation passed');
    
    try {
      const userId = 'current-user'; // TODO: Get from auth context
      
      const patientData = {
        ...formData,
        admissionDate: new Date(formData.admissionDate),
        expectedDischargeDate: formData.expectedDischargeDate ? new Date(formData.expectedDischargeDate) : null
      };
      
      console.log('📝 Patient data prepared:', patientData);
      
      let savedPatientId;
      if (patientId) {
        // Update existing patient
        console.log('🔄 Updating patient:', patientId);
        await updatePatient(patientId, patientData, userId);
        savedPatientId = patientId;
      } else {
        // Create new patient
        console.log('➕ Creating new patient');
        savedPatientId = await createPatient(patientData, userId);
        console.log('✅ Patient created with ID:', savedPatientId);
      }
      
      console.log('📞 Calling onSave with ID:', savedPatientId);
      onSave(savedPatientId);
      console.log('✅ onSave completed');
    } catch (error) {
      console.error('❌ Error saving patient:', error);
      // Error will be handled by parent component or notification system
      setErrors({ general: t('failedToSavePatient') });
    }
  };
  
  const getRiskFactorsByCategory = (category) => {
    return availableRiskFactors.filter(f => f.category === category);
  };
  
  return (
    <div className="patient-form-overlay">
      <div className="patient-form">
        <div className="patient-form-header">
          <div>
            <h2>
              {patientId ? 'Edit Patient' : 'Add New Patient'}
            </h2>
            <p>Manage patient information and risk factors</p>
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            <X size={20} />
          </button>
        </div>
        
        <div className="patient-form-content">
          {/* General Error Display */}
          {errors.general && (
            <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
              <AlertTriangle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              {errors.general}
            </div>
          )}
          
          {/* Basic Information */}
          <div className="form-section">
            <h3>{t('basicInformation')}</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="patientIdentifier">{t('patientIdentifierLabel')}</label>
                <input
                  type="text"
                  id="patientIdentifier"
                  value={formData.patientIdentifier}
                  onChange={(e) => handleInputChange('patientIdentifier', e.target.value)}
                  className={errors.patientIdentifier ? 'error' : ''}
                  placeholder={t('placeholderPatientId')}
                />
                {errors.patientIdentifier && (
                  <span className="error-text">{errors.patientIdentifier}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="ageRange">{t('ageRangeLabel')}</label>
                <select
                  id="ageRange"
                  value={formData.ageRange}
                  onChange={(e) => handleInputChange('ageRange', e.target.value)}
                >
                  <option value="<18">{t('ageUnder18')}</option>
                  <option value="18-65">{t('age18to65')}</option>
                  <option value=">65">{t('ageOver65')}</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="admissionDate">{t('admissionDate')} *</label>
                <input
                  type="date"
                  id="admissionDate"
                  value={formData.admissionDate}
                  onChange={(e) => handleInputChange('admissionDate', e.target.value)}
                  className={errors.admissionDate ? 'error' : ''}
                />
                {errors.admissionDate && (
                  <span className="error-text">{errors.admissionDate}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="expectedDischargeDate">{t('expectedDischargeDate')}</label>
                <input
                  type="date"
                  id="expectedDischargeDate"
                  value={formData.expectedDischargeDate}
                  onChange={(e) => handleInputChange('expectedDischargeDate', e.target.value)}
                />
              </div>
            </div>
          </div>
          
          {/* Location Information */}
          <div className="form-section">
            <h3>{t('locationAssignment')}</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="departmentId">{t('departmentLabel')}</label>
                <select
                  id="departmentId"
                  value={formData.departmentId}
                  onChange={(e) => handleDepartmentChange(e.target.value)}
                  className={errors.departmentId ? 'error' : ''}
                >
                  <option value="">{t('selectDepartment')}</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {errors.departmentId && (
                  <span className="error-text">{errors.departmentId}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="roomId">{t('roomLabel')}</label>
                <select
                  id="roomId"
                  value={formData.roomId}
                  onChange={(e) => handleRoomChange(e.target.value)}
                  className={errors.roomId ? 'error' : ''}
                  disabled={!formData.departmentId}
                >
                  <option value="">{t('selectRoom')}</option>
                  {rooms.map(room => (
                    <option key={room.id} value={room.id}>
                      {room.number || room.name}
                    </option>
                  ))}
                </select>
                {errors.roomId && (
                  <span className="error-text">{errors.roomId}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="bedId">Bed *</label>
                <select
                  id="bedId"
                  value={formData.bedId}
                  onChange={(e) => handleInputChange('bedId', e.target.value)}
                  className={errors.bedId ? 'error' : ''}
                  disabled={!formData.roomId}
                >
                  <option value="">Select Bed</option>
                  {beds.map(bed => (
                    <option key={bed.id} value={bed.id}>
                      {bed.number} {bed.occupied && bed.id !== formData.bedId ? '(Occupied)' : ''}
                    </option>
                  ))}
                </select>
                {errors.bedId && (
                  <span className="error-text">{errors.bedId}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="isolationLevel">{t('isolationLevelLabel')}</label>
                <select
                  id="isolationLevel"
                  value={formData.isolationLevel}
                  onChange={(e) => handleInputChange('isolationLevel', e.target.value)}
                >
                  <option value="None">{t('isolationNone')}</option>
                  <option value="Standard">{t('isolationStandard')}</option>
                  <option value="Contact">{t('isolationContact')}</option>
                  <option value="Droplet">{t('isolationDroplet')}</option>
                  <option value="Airborne">{t('isolationAirborne')}</option>
                  <option value="Contact+Droplet">{t('isolationContactDroplet')}</option>
                  <option value="Contact+Airborne">{t('isolationContactAirborne')}</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Risk Factors */}
          <div className="form-section">
            <h3>
              <AlertTriangle size={20} />
              Risk Factors
            </h3>
            <p className="section-description">
              Select applicable risk factors that affect infection control risk assessment
            </p>
            
            {factorsLoading ? (
              <div className="loading">Loading risk factors...</div>
            ) : (
              <div className="risk-factors-grid">
                {['Demographic', 'Clinical', 'Procedural', 'Comorbidity'].map(category => {
                  const categoryFactors = getRiskFactorsByCategory(category);
                  
                  if (categoryFactors.length === 0) return null;
                  
                  return (
                    <div key={category} className="risk-factor-category">
                      <h4>{category}</h4>
                      <div className="risk-factor-list">
                        {categoryFactors.map(factor => (
                          <label key={factor.id} className="risk-factor-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.riskFactorIds.includes(factor.id)}
                              onChange={() => handleRiskFactorToggle(factor.id)}
                            />
                            <span className="factor-name">{factor.name}</span>
                            <span className="factor-multiplier">×{factor.multiplier.toFixed(1)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {formData.riskFactorIds.length > 0 && (
              <div className="selected-factors-summary">
                {t('riskFactorsSelected', { count: formData.riskFactorIds.length })}
              </div>
            )}
          </div>
          
          {/* Notes */}
          <div className="form-section">
            <h3>{t('additionalNotes')}</h3>
            <div className="form-group full-width">
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                placeholder={t('placeholderNotes')}
              />
            </div>
          </div>
        </div>
        
        <div className="patient-form-footer">
          <button onClick={onClose} className="btn btn-cancel">
            {t('cancel')}
          </button>
          <button 
            onClick={handleSave} 
            className="btn btn-save"
            disabled={saving}
          >
            {saving ? t('savingLabel') : (
              <>
                {patientId ? <Save size={16} /> : <UserPlus size={16} />}
                {patientId ? t('updatePatient') : t('addPatient')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatientForm;

